import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchInwardCSRFDetails, fetchInwardDetails, fetchInwardOfficeDetails } from "../../fetchHandler";
import { fetchQuery } from "../../../../utils/commonComponent/fetched";
import { Autocomplete, Box, Checkbox, TextField, Typography } from "@mui/material";
import LoaderModal from "../../../../utils/loader.js/loader";
import { AddButton } from "../../../../utils/commonComponent/Custom/Button";
import { DispatchModeOptions } from "../../../disciplineMaster/disiplineConst";
import { getPermissions } from "../../../rbac/hasAccess";
import InwardTestTableCommom from "../InwardTestTableCommom";
import { fetchUsers } from "../../../datasheet/fetchHandler";
import { CSRFFormData } from "../../csrfForm";
import { generateCSRFPDFInward } from "./inwardCSRFPdf";

const InwardCSRFView = (props) => {
  const params = useParams();
  const permissions = getPermissions(props.moduleId);
  const [letterHead, setLetterHead] = React.useState(true);
  const [electronicSign, setElectronicSign] = React.useState(true);
  const [signImg, setSignImg] = React.useState(false);
  const [userList, setUserList] = useState([]);

  const [inputStates, setInputStates] = useState({});
  const [officeFormData, setOfficeFormData] = useState({});
  const [jobDetails, setJobDetails] = useState({});
  const [testRows, setTestRows] = React.useState([]);
  const [loading, setLoading] = useState(false);
  const [CSRFFetchData, setCSRFFetchData] = React.useState({});
  const [inwardTestCommon, setinwardTestCommon] = useState([]);
  const [isCSRF, setIsCSRF] = useState(true);
  
  const mergedCSRFData = React.useMemo(() => {
    if (!CSRFFetchData || Object.keys(CSRFFetchData).length === 0) return [];
    return CSRFFormData.map((item, index) => {
      const fetchedQuestion = CSRFFetchData[`quession${index + 1}`];
      const { answer, details } = fetchedQuestion
        ? JSON.parse(fetchedQuestion)
        : { answer: "", details: "" };
      return { ...item, answer, details };
    });
  }, [CSRFFetchData]);

  useEffect(() => {
    if (params.id != 0) {
      fetchInwardDetails(params, setInputStates);
      fetchInwardOfficeDetails(params, setOfficeFormData);
      fetchInwardCSRFDetails(params, setCSRFFetchData);
    }
  }, [params?.id]);

  const fetchInwardTestRows = async () => {
    try {
      // 1) fetch inward rows
      const inwardTestRows = await fetchQuery(`select * from inwardTests where status = 1 and inwardId = ${params.id}`);

      if (!Array.isArray(inwardTestRows) || inwardTestRows.length === 0) {
        setTestRows([]);
        return;
      }

      // 2) Extract all unique test IDs and material IDs (handling comma-separated values)
      const allTestIds = new Set();
      const materialIds = new Set();
      inwardTestRows.forEach(row => {
        //  Split materialId by comma
        if (row.materialId) {
          String(row.materialId).split(',').map(id => id.trim()).filter(Boolean).forEach(id => materialIds.add(id));
        }
        if (row.testId) {
          String(row.testId).split(',').map(id => id.trim()).filter(Boolean).forEach(id => allTestIds.add(id));
        }
      });

      // 3) fetch test methods for all test IDs
      let testMethod = [];
      if (allTestIds.size > 0) {
        const testIdsQuery = ` and id in (${Array.from(allTestIds).join(",")})`;
        try {
          testMethod = await fetchQuery("select * from materialTestings where status = 1" + testIdsQuery);
        } catch (e) {
          console.warn("Failed to fetch by test IDs, trying material IDs fallback");
        }
      }

      // fallback: if nothing returned, fetch by materialIds
      if ((!Array.isArray(testMethod) || testMethod.length === 0) && materialIds.size > 0) {
        const materialIdsQuery = ` and materialId in (${Array.from(materialIds).join(",")})`;
        testMethod = await fetchQuery("select * from materialTestings where status = 1" + materialIdsQuery);
      }

      // 4) fetch material details
      let materials = [];
      if (materialIds.size > 0) {
        try {
          const materialQuery = ` and id in (${Array.from(materialIds).join(",")})`;
          materials = await fetchQuery("select * from materialScope where status = 1" + materialQuery);
        } catch (e) {
          console.error(`Error fetching material:`, e);
          return null;
        }
      }
      // 5) build lookup maps
      const testMethodMap = (testMethod || []).reduce((acc, t) => {
        acc[String(t.id)] = t;
        return acc;
      }, {});

      const materialDetailsMap = (materials || []).reduce((acc, m) => {
        acc[String(m.id)] = m.MaterialName ?? m.materialName ?? m.name ?? "";
        return acc;
      }, {});

      // 6) compose enhanced rows
      const enhancedInwardTestRows = inwardTestRows.map((row) => {
        const testDetails = {};

        if (row.testId) {
          const testIds = String(row.testId).split(',').map(id => id.trim());
          testIds.forEach(testId => {
            if (testId && testMethodMap[testId]) {
              testDetails[testId] = testMethodMap[testId];
            }
          });
        }

        const resolvedMaterialNames = row.materialId
          ? String(row.materialId)
              .split(",")
              .map((id) => id.trim())
              .filter(Boolean)
              .map((id) => materialDetailsMap[id] ?? "")
              .filter(Boolean)
              .join(",")
          : "";

        return {
          ...row,
          testDetails,
          materialName: resolvedMaterialNames,
          //  Pass full map so component can resolve per-test materialId → name
          materialNamesMap: materialDetailsMap,
          testMethod: Object.values(testDetails)[0]?.testMethod ?? "",
          testName: Object.values(testDetails)[0]?.testName ?? "",
        };
      });

      setTestRows(enhancedInwardTestRows);

      // Build common values
    const getUniqueValues = (key) =>
      [...new Set(
        enhancedInwardTestRows
          .map((row) => row[key])
          .filter((value) => value && String(value).trim() !== "")
      )].join(", ");

    setinwardTestCommon({
      sampleCondition: getUniqueValues("sampleCondition"),
      sampleType: getUniqueValues("sampleType"),
      locationChainageNo: getUniqueValues("locationChainageNo"),
      SamplingDetails: getUniqueValues("SamplingDetails"),
      quantity: getUniqueValues("quantity"),
      lotNumber: getUniqueValues("lotNumber"),
    });
    } catch (error) {
      console.error("Error fetching test master data:", error);
    }
  };

  const fetchJobDetails = async () => {
    try {
      const [jobDetailsById] = await fetchQuery(`select * from jobRegister where id = ${inputStates?.jobId}`);
      setJobDetails(jobDetailsById);
    } catch (error) {
      console.error("Error fetching job details:", error);
    }
  };

  useEffect(() => {
    if (inputStates?.jobId) {
      fetchJobDetails();
    }
    fetchInwardTestRows();
  }, [inputStates?.jobId]);

  

  useEffect(() => {
    fetchUsers().then((users) => {
      setUserList(users);
    });
  }, []);

  return (
    <div className="max-w-7xl mx-auto p-4 text-sm text-black">
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-5">
        {(permissions.pdfDownload || permissions.allAccess) && (
          <div className="flex flex-col sm:flex-row flex-wrap gap-4 w-full lg:w-auto">
            <div className="w-full sm:w-[220px]">
              <Autocomplete
                size="small"
                id="quotation_view_letterhead"
                options={[
                  { id: 1, label: "With Letter Head" },
                  { id: 2, label: "Without Letter Head" },
                ]}
                renderInput={(params) => (
                  <TextField {...params} label="Letter Head" fullWidth />
                )}
                onInputChange={(event, newInputValue) => {
                  if (
                    newInputValue === "With Letter Head" ||
                    newInputValue === ""
                  ) {
                    setLetterHead(true);
                  } else if (newInputValue === "Without Letter Head") {
                    setLetterHead(false);
                  }
                }}
              />
            </div>

            <div className="w-full sm:w-[220px]">
              <Autocomplete
                size="small"
                id="quotation_view__electronicsign"
                options={[
                  { id: 1, label: "With Electronically Signed" },
                  { id: 2, label: "Without Electronically Signed" },
                  { id: 3, label: "Signature Img" },
                ]}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Electronically Signed"
                    fullWidth
                  />
                )}
                onInputChange={(event, newInputValue) => {
                  if (
                    newInputValue === "With Electronically Signed" ||
                    newInputValue === ""
                  ) {
                    setElectronicSign(true);
                    setSignImg(true);
                  } else if (
                    newInputValue === "Without Electronically Signed"
                  ) {
                    setElectronicSign(true);
                    setSignImg(false);
                  } else if (newInputValue === "Signature Img") {
                    setElectronicSign(false);
                    setSignImg(true);
                  }
                }}
              />
            </div>

            <div className="w-full sm:w-auto">
              <AddButton
                label="Download PDF"
                id="quotation_view_generatepdf"
                onClick={() =>
                  generateCSRFPDFInward({
                    letterHead,
                    jobDetails,
                    inputStates,
                    testRows,
                    officeFormData,
                    setLoading,
                    userList,
                    electronicSign,
                    signImg,
                    mergedCSRFData,
                    inwardTestCommon,
                    isCSRF,
                  })
                }
              />
            </div>
          </div>
        )}

        <div className="w-full lg:w-auto">
          <AddButton
            label="Back"
            id="quotation_view_back"
            variant="cancel"
            onClick={() => window.history.back()}
          />
        </div>
      </div>

      <h1 className="text-center font-bold mb-5 text-[2rem]">Test Request Form</h1>

      <table className="w-full border border-black border-collapse mb-8">
        <tr>
          <td className="border border-black p-1 font-semibold">Job Number</td>
          <td className="border border-black p-1">{jobDetails?.jobNumber || ""}</td>
          <td className="border border-black p-1 font-semibold">Lab Code</td>
          <td className="border border-black p-1">{inputStates?.labCode || ""}</td>
        </tr>

        <tr>
          <td className="border border-black p-1 font-semibold">Name & Address of Customer</td>
          <td colSpan="3" className="border border-black p-1">{inputStates?.customerName}
            <br />
            {inputStates?.address}
          </td>
        </tr>

        <tr>
          <td className="border border-black p-1 font-semibold">Sample Description</td>
          <td colSpan="3" className="border border-black p-1">
            {[...new Set(testRows?.flatMap(row =>
              (row.materialName || "")
                ?.split(",")
                ?.map(name => name.trim())
                ?.filter(Boolean)
            ))].join(", ")}
          </td>
        </tr>

        <tr>
          <td className="border border-black p-1 font-semibold">No. of sample(s) received</td>
          <td className="border border-black p-1" colSpan="3">{inputStates?.sampleReceived || ""} Nos.</td>
        </tr>

        <tr>
          <td className="border border-black p-1 font-semibold">Marks on the sample</td>
          <td colSpan="3" className="border border-black p-1">{inputStates?.sampleMarks || ""}</td>
        </tr>

        <tr>
          <td className="border border-black p-1 font-semibold">Customer Reference</td>
          <td colSpan="3" className="border border-black p-1">{inputStates?.customerReference || ""}</td>
        </tr>

        <tr>
          <td className="border border-black p-1 font-semibold">Quantity of sample received</td>
          <td colSpan="3" className="border border-black p-1">{inwardTestCommon?.quantity}</td>
        </tr>

        <tr>
          <td className="border border-black p-1 font-semibold">Sample Condition</td>
          <td colSpan="3" className="border border-black p-1">{inwardTestCommon?.sampleCondition}</td>
        </tr>

        <tr>
          <td className="border border-black p-1 font-semibold">Sample details (if any)</td>
          <td colSpan="3" className="border border-black p-1">{inwardTestCommon?.SamplingDetails}</td>
        </tr>
      </table>

      {/* ====== call common testRow component ====== */}
      <InwardTestTableCommom testRows={testRows} isCSRF={isCSRF} />

      <table className="w-full border border-black border-collapse text-xs mt-2">
        <tbody>
          <tr>
            <td className="p-1 border border-black font-bold">Mode of delivery:</td>
            <td className="p-1 border border-black" colSpan={2}>
              {DispatchModeOptions.find((option) => option.id == inputStates?.dispatchMode)?.label || ""}</td>

            <td className="p-1 border border-black font-bold">Packing Condition of Sample:</td>
            <td className="p-1 border border-black" colSpan={2}></td>
          </tr>

          <tr>
            <td className="p-1 border border-black font-bold">Submitted By:</td>
            <td className="p-1 border border-black"></td>

            <td className="p-1 border border-black font-bold">Date of Submission:</td>
            <td className="p-1 border border-black"></td>
            
            <td className="p-1 border border-black font-bold">Signature:</td>
            <td className="p-1 border border-black"></td>

          </tr>

          <tr>
            <td className="p-1 border border-black font-bold">Received by:</td>
            <td className="p-1 border border-black"></td>

            <td className="p-1 border border-black font-bold">Received on:</td>
            <td className="p-1 border border-black"></td>

            <td className="p-1 border border-black font-bold">Receiver Signature:</td>
            <td className="p-1 border border-black"></td>
          </tr>
        </tbody>
      </table>

      <Box
        sx={{
          fontSize: "13px",
          display: "flex",
          flexDirection: "column",
          textAlign: "left",
          marginTop: "10px",
        }}
      >
        <h1><b>Review of the sample received:</b></h1>
        <table style={{ border: "none" }}>
          <tbody>
            {mergedCSRFData?.map((item, index) => (
              <tr
                key={index}
                style={{ pageBreakInside: "avoid" }}
                id="pagbreaksborder"
              >
                {!item?.hasPoints && (
                  <td style={{width: "40%", padding: "0px 5px", border: "1px solid black", borderBottom: "none", borderRight: "none", verticalAlign: "top" }}>
                    <Typography variant="span">
                      {index + 1}.{item?.question}
                    </Typography>
                  </td>
                )}

                {item?.hasPoints && (
                  <td style={{width: "40%", padding: "0px 5px", border: "1px solid black", borderBottom: "none", borderRight: "none", verticalAlign: "top" }}>
                    <Typography variant="span">
                      <b>{index + 1}.{item?.question}</b>
                    </Typography>
                    {item?.points?.map((option, index) => (
                      <Typography variant="caption" display="block" key={index}>
                        {option}
                      </Typography>
                    ))}
                  </td>
                )}
                <td style={{ border: "1px solid black", borderBottom: "none", width: "60%", verticalAlign: "top", padding: "0px 5px"}}>
                  {item?.options?.map((option, optionIndex) => (
                    <span key={optionIndex}>
                      <Checkbox
                        size="small"
                        sx={{ m: "0px", p: "0px 5px" }}
                        checked={item?.answer === option}
                      />
                      {option}
                    </span>
                  ))}
                  {item?.hasDetails && (
                    <div style={{ border: "none", display: "flex", alignItems: "center", paddingLeft: "5px"}}>
                      <Typography variant="span">{item?.details}</Typography>
                    </div>
                  )}
                  {item?.hasoptions && (
                    <div style={{ border: "none", display: "flex", alignItems: "center",}}>
                      {item?.options1.map((option, optionIndex) => (
                        <span key={optionIndex}>
                          <Checkbox
                            size="small"
                            sx={{ m: "0px", p: "0px 5px" }}
                            checked={item?.details === option}
                          />
                          {option}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            <tr id="pagbreaksborder">
              <td colSpan={2} style={{border: "1px solid black", borderRight: "none", borderBottom: "none",}}></td>
            </tr>
          </tbody>
        </table>
      </Box>

      <LoaderModal loading={loading} />
    </div>
  );
};

export default InwardCSRFView;